import { create } from 'zustand'
import type { PlatformEvent, EventFilter, EventStreamStats } from './types'
import { timelineTimestamp } from './lib/eventTimestamps'

interface EventState {
  events: PlatformEvent[]
  isConnected: boolean
  isLoading: boolean
  isLoadingHistory: boolean
  hasMoreHistory: boolean
  oldestLoadedTimestamp: number | null
  eventIds: Set<string>
  error: string | null
  filter: EventFilter
  stats: EventStreamStats
}

interface EventActions {
  addEvent: (event: PlatformEvent) => void
  addEvents: (events: PlatformEvent[]) => void
  appendHistoricalEvents: (events: PlatformEvent[], hasMore: boolean) => void
  setConnected: (connected: boolean) => void
  setLoading: (loading: boolean) => void
  setLoadingHistory: (loading: boolean) => void
  setError: (error: string | null) => void
  setFilter: (filter: Partial<EventFilter>) => void
  clearFilter: () => void
  clearEvents: () => void
  getFilteredEvents: () => PlatformEvent[]
  getEventsForCustomer: (customerId: string) => PlatformEvent[]
  getOldestTimestamp: () => number | null
}

const MAX_EVENTS = 1000 // Keep last 1000 events in memory

export const useEventStore = create<EventState & EventActions>((set, get) => ({
  // State
  events: [],
  isConnected: false,
  isLoading: false,
  isLoadingHistory: false,
  hasMoreHistory: true,
  oldestLoadedTimestamp: null,
  eventIds: new Set<string>(),
  error: null,
  filter: {},
  stats: {
    eventsPerSecond: 0,
    totalEvents: 0,
    uniqueCustomers: 0,
    topEventTypes: [],
  },

  // Actions
  addEvent: (event) => {
    set((state) => {
      // Skip duplicates
      if (state.eventIds.has(event.id)) {
        console.log('[EventDebug] store.addEvent: dropping duplicate', { id: event.id })
        return state
      }
      console.log('[EventDebug] store.addEvent (SSE):', {
        id: event.id,
        type: event.type,
        hasDataObject: event?.data?.object != null,
        customer_id: event.customer_id,
      })
      // Prepend new streaming event (appears at top)
      const newEvents = [event, ...state.events].slice(0, MAX_EVENTS)
      const newIds = new Set(state.eventIds)
      newIds.add(event.id)
      return {
        events: newEvents,
        eventIds: newIds,
        stats: calculateStats(newEvents),
      }
    })
  },

  addEvents: (events) => {
    set((state) => {
      const newIds = new Set(state.eventIds)
      const uniqueEvents = events.filter((e) => {
        if (newIds.has(e.id)) return false
        newIds.add(e.id)
        return true
      })
      const newEvents = [...uniqueEvents, ...state.events].slice(0, MAX_EVENTS)
      return {
        events: newEvents,
        eventIds: newIds,
        stats: calculateStats(newEvents),
      }
    })
  },

  // Append older historical events to the end (appear below current events)
  appendHistoricalEvents: (events, hasMore) => {
    set((state) => {
      const newIds = new Set(state.eventIds)
      // Filter out duplicates and sort by occurred_at (falling back to
      // created) so historic ingestions land in true timeline position.
      const uniqueEvents = events
        .filter((e) => {
          if (newIds.has(e.id)) return false
          newIds.add(e.id)
          return true
        })
        .sort((a, b) => timelineTimestamp(b) - timelineTimestamp(a))

      // Diagnostic: how many of the incoming events have data.object? This is
      // the load that populates the timeline on first render and via the
      // 30s auto-refresh, so a low ratio here directly explains the
      // "everything renders as #abc12345" symptom.
      const incomingTotal = events.length
      let incomingWithDataObject = 0
      for (const e of events) {
        if (e?.data?.object != null) incomingWithDataObject++
      }
      console.log('[EventDebug] store.appendHistoricalEvents:', {
        incomingTotal,
        incomingWithDataObject,
        uniqueAfterDedupe: uniqueEvents.length,
        droppedAsDuplicate: incomingTotal - uniqueEvents.length,
        hasMore,
      })

      if (uniqueEvents.length === 0) {
        return {
          isLoadingHistory: false,
          hasMoreHistory: hasMore,
        }
      }

      // Append to end (historical events are older, appear below)
      const newEvents = [...state.events, ...uniqueEvents].slice(0, MAX_EVENTS)

      // Track oldest timestamp for pagination
      const oldestEvent = uniqueEvents[uniqueEvents.length - 1]
      const oldestTimestamp = oldestEvent ? oldestEvent.created : state.oldestLoadedTimestamp

      return {
        events: newEvents,
        eventIds: newIds,
        oldestLoadedTimestamp: oldestTimestamp,
        hasMoreHistory: hasMore && newEvents.length < MAX_EVENTS,
        isLoadingHistory: false,
        stats: calculateStats(newEvents),
      }
    })
  },

  setConnected: (connected) => set({ isConnected: connected }),

  setLoading: (loading) => set({ isLoading: loading }),

  setLoadingHistory: (loading) => set({ isLoadingHistory: loading }),

  setError: (error) => set({ error }),

  setFilter: (filter) =>
    set((state) => ({
      filter: { ...state.filter, ...filter },
    })),

  clearFilter: () => set({ filter: {} }),

  clearEvents: () =>
    set({
      events: [],
      eventIds: new Set<string>(),
      oldestLoadedTimestamp: null,
      hasMoreHistory: true,
      stats: {
        eventsPerSecond: 0,
        totalEvents: 0,
        uniqueCustomers: 0,
        topEventTypes: [],
      },
    }),

  getFilteredEvents: () => {
    const { events, filter } = get()
    return events.filter((event) => {
      if (filter.type) {
        const types = Array.isArray(filter.type) ? filter.type : [filter.type]
        if (types.length > 0 && !types.some((t) => event.type.includes(t))) return false
      }
      if (filter.category) {
        const categories = Array.isArray(filter.category) ? filter.category : [filter.category]
        if (categories.length > 0 && !categories.includes(event.category)) return false
      }
      if (filter.origin) {
        const origins = Array.isArray(filter.origin) ? filter.origin : [filter.origin]
        if (origins.length > 0 && !origins.includes(event.origin)) return false
      }
      if (filter.customerId && event.customer_id !== filter.customerId) return false
      if (filter.source) {
        const sources = Array.isArray(filter.source) ? filter.source : [filter.source]
        if (sources.length > 0 && !sources.includes(event.source)) return false
      }
      if (filter.sessionId && event.session_id !== filter.sessionId) return false
      if (filter.createdAfter && event.created < filter.createdAfter) return false
      if (filter.createdBefore && event.created > filter.createdBefore) return false
      return true
    })
  },

  getEventsForCustomer: (customerId: string) => {
    const { events } = get()
    return events.filter((event) => event.customer_id === customerId)
  },

  getOldestTimestamp: () => get().oldestLoadedTimestamp,
}))

function calculateStats(events: PlatformEvent[]): EventStreamStats {
  const now = Date.now() / 1000
  const oneMinuteAgo = now - 60

  // Events in the last minute for EPS calculation
  const recentEvents = events.filter((e) => e.created > oneMinuteAgo)
  const eventsPerSecond = recentEvents.length / 60

  // Unique customers
  const customerIds = new Set(events.map((e) => e.customer_id).filter(Boolean))

  // Top event types
  const typeCounts = new Map<string, number>()
  for (const event of events) {
    typeCounts.set(event.type, (typeCounts.get(event.type) || 0) + 1)
  }
  const topEventTypes = Array.from(typeCounts.entries())
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)

  return {
    eventsPerSecond: Math.round(eventsPerSecond * 10) / 10,
    totalEvents: events.length,
    uniqueCustomers: customerIds.size,
    topEventTypes,
  }
}
